import React, { useState, useEffect, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import client from '../../../lib/api/client';
import HButton from '../../common/HButton';
import SVG from '../../common/svg';

const AnimationStyles = createGlobalStyle`
  .success-animation-enter {
    opacity: 0;
    transform: scale(0.9);
  }
  .success-animation-enter-active {
    opacity: 1;
    transform: scale(1);
    transition: opacity 300ms, transform 300ms;
  }
  .success-animation-exit {
    opacity: 1;
    transform: scale(1);
  }
  .success-animation-exit-active {
    opacity: 0;
    transform: scale(0.9);
    transition: opacity 300ms, transform 300ms;
  }
`;

const generateSystemId = (length = 52) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = 'MOB';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const QRCodeScanner = ({ show, onClose, user, onRegisterSuccess }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [registeredUdi, setRegisteredUdi] = useState('');
  const videoRef = useRef(null);
  const codeReader = new BrowserMultiFormatReader();

  const handleScan = async (rawScannedUdi) => {
    if (!rawScannedUdi) return;
    const scannedUdi = rawScannedUdi.replace(/[^\x20-\x7E]+/g, '');
    const payloadStep1 = { udi: scannedUdi, userData: user };
    await client.post('/v1/camera/udi/register/', payloadStep1);
    const payloadStep2 = {
      udi: scannedUdi,
      system_id: generateSystemId(),
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      country: user.country,
      installed_date: new Date().toISOString().slice(0, 10),
      bypass_signature: true,
    };
    await client.post('/qc/v2.5/device/register/', payloadStep2, { baseURL: '/' });

    setRegisteredUdi(scannedUdi);
    setScanSuccess(true);

    if (typeof onRegisterSuccess === 'function') {
      onRegisterSuccess();
    }
  };

  const stopScanner = () => {
    codeReader.reset();
    onClose();
  };

  useEffect(() => {
    if (show && videoRef.current) {
      setScanSuccess(false);
      setRegisteredUdi('');
      setIsScanning(false);

      codeReader.decodeFromVideoDevice(
        undefined,
        videoRef.current,
        async (result, err) => {
          if (result && !isScanning) {
            setIsScanning(true);
            codeReader.stopContinuousDecode();
            try {
              const udiText = result.getText();
              await handleScan(udiText);

              const match = udiText.match(/\(21\)(.*)/);
              const serialNumber = match ? match[1] : udiText;

              setRegisteredUdi(serialNumber);
              setScanSuccess(true);

              setTimeout(() => stopScanner(), 6000);
            } catch (error) {
              stopScanner();
            }
          }
          if (err && !(err instanceof NotFoundException)) {
            stopScanner();
          }
        },
      );
    }
    return () => codeReader.reset();
  }, [show]);

  if (!show) return null;

  return (
    <>
      <AnimationStyles />
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.7)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            background: '#fff',
            padding: 20,
            borderRadius: 8,
            width: '80%',
            maxWidth: '500px',
            textAlign: 'center',
            overflow: 'hidden',
          }}
        >
          <CSSTransition
            in={scanSuccess}
            timeout={300}
            classNames="success-animation"
            unmountOnExit
          >
            <div>
              <SVG name="check" width="80" height="80" color="green" />
              <h3 style={{ color: '#333', marginTop: '20px', marginBottom: '5px' }}>
                Device Registered!
              </h3>
              <p style={{ color: '#666', wordBreak: 'break-all' }}>
                S/N: {registeredUdi}
              </p>
            </div>
          </CSSTransition>
          {!scanSuccess && (
            <>
              <p
                style={{
                  color: '#59616a',
                  fontSize: '16px',
                  marginBottom: '16px',
                  fontFamily: 'Source Sans Pro',
                  fontWeight: '500',
                }}
              >
                To register your device, scan the QR-Code on the box
              </p>
              <video ref={videoRef} style={{ width: '100%', height: 'auto' }} />
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                <HButton
                  category="Secondary"
                  size="Medium"
                  text="Cancel"
                  onClick={stopScanner}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default QRCodeScanner;
